/**
 * A session's meeting, and meetings that arrived with no session to land on.
 *
 * Brief K slice 2a, decision K7; migration 871. A session holds one Zoom, Meet
 * or Teams meeting link. When that meeting ends with a transcript, the
 * integrations engine records it and the database writes the transcript as the
 * session's document (the same `session_artifacts` row a pasted transcript is).
 * A finished meeting no session holds waits in a short list for a person to
 * attach here or set aside — nothing is attached by guess.
 *
 * Every write is one of the 871 functions; nothing here writes a table.
 *
 * Untyped until db:types after prod apply: `session_meeting_refs`,
 * `meeting_transcript_arrivals` and the four functions are not yet in
 * src/types/database.generated.ts (it is generated from production), so the
 * rows are narrowed by hand below.
 */
import { getSafeSupabaseClient } from './supabaseSync';

export type MeetingProvider = 'zoom' | 'google-meet' | 'microsoft-teams';

export const MEETING_PROVIDER_LABELS: Record<MeetingProvider, string> = {
  zoom: 'Zoom',
  'google-meet': 'Google Meet',
  'microsoft-teams': 'Teams',
};

export interface SessionMeeting {
  provider: MeetingProvider;
  meetingRef: string;
  meetingUrl: string | null;
}

export interface WaitingMeeting {
  id: string;
  provider: MeetingProvider;
  topic: string | null;
  startedAt: string | null;
  meetingRef: string;
}

export async function getSessionMeeting(sessionId: string): Promise<SessionMeeting | null> {
  const db = getSafeSupabaseClient();
  if (!db) return null;
  const { data, error } = await db
    .from('session_meeting_refs')
    .select('provider, meeting_ref, meeting_url')
    .eq('session_id', sessionId)
    .maybeSingle();
  if (error || !data) return null;
  const row = data as { provider: MeetingProvider; meeting_ref: string; meeting_url: string | null };
  return { provider: row.provider, meetingRef: row.meeting_ref, meetingUrl: row.meeting_url };
}

/** Link (or, with empty text, unlink) the session's meeting. Throws the database's own sentence. */
export async function setSessionMeeting(sessionId: string, meeting: string | null): Promise<void> {
  const db = getSafeSupabaseClient();
  if (!db) throw new Error('Not connected.');
  const { error } = await db.rpc('set_session_meeting', { p_session_id: sessionId, p_meeting: meeting ?? '' });
  if (error) throw new Error(error.message);
}

export async function listWaitingMeetings(workspaceId: string, limit = 10): Promise<WaitingMeeting[]> {
  const db = getSafeSupabaseClient();
  if (!db) return [];
  const { data, error } = await db
    .from('meeting_transcript_arrivals')
    .select('id, provider, topic, started_at, meeting_ref')
    .eq('workspace_id', workspaceId)
    .eq('status', 'waiting')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  return (data as Array<{ id: string; provider: MeetingProvider; topic: string | null; started_at: string | null; meeting_ref: string }>).map((r) => ({
    id: r.id,
    provider: r.provider,
    topic: r.topic,
    startedAt: r.started_at,
    meetingRef: r.meeting_ref,
  }));
}

export async function attachWaitingMeeting(arrivalId: string, sessionId: string): Promise<void> {
  const db = getSafeSupabaseClient();
  if (!db) throw new Error('Not connected.');
  const { error } = await db.rpc('attach_meeting_transcript', { p_arrival_id: arrivalId, p_session_id: sessionId });
  if (error) throw new Error(error.message);
}

export async function setWaitingMeetingAside(arrivalId: string): Promise<void> {
  const db = getSafeSupabaseClient();
  if (!db) throw new Error('Not connected.');
  const { error } = await db.rpc('dismiss_meeting_transcript', { p_arrival_id: arrivalId });
  if (error) throw new Error(error.message);
}
